// src/pages/Platform/Tabs/Chatbot/ChatbotWidget/MessageDisplay.js

import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import DOMPurify from 'dompurify';
import axios from 'axios';
import { InlineForm } from '../ChatbotBuilder/KnowledgeBase/Forms';
import LinkCarousel from './LinkCarousel';

// Helper to extract URLs from text
const extractUrls = (text) => {
  if (!text) return [];
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const matches = [...text.matchAll(urlRegex)];
  const urls = matches.map(match => {
    let url = match[1];
    // Clean trailing punctuation common in markdown/text
    // e.g. [text](url) -> url)
    // text ended with url. -> url.

    // Iteratively strip trailing characters that are likely punctuation
    while (url.length > 0) {
      const lastChar = url.charAt(url.length - 1);
      if (/[.,;!?]/.test(lastChar)) {
        url = url.slice(0, -1);
      } else if (lastChar === ')') {
        // Count parens to handle valid parens in URLs vs markdown closing paren
        const open = (url.match(/\(/g) || []).length;
        const close = (url.match(/\)/g) || []).length;
        if (close > open) {
          url = url.slice(0, -1);
        } else {
          break;
        }
      } else if (lastChar === ']') {
        url = url.slice(0, -1);
      } else {
        break;
      }
    }
    return url;
  });

  return [...new Set(urls.filter(u => u.length > 10))]; // Valid URL min length check
};

const MessageItem = ({
  msg,
  index,
  botMessageBoxColor,
  botMessageTextColor,
  userMessageBoxColor,
  userMessageTextColor,
  handleFormSubmit
}) => {
  const [links, setLinks] = useState([]);
  const scrapedUrls = React.useRef(new Set());

  useEffect(() => {
    // Only fetch links for assistant messages that aren't streaming
    if (msg.role !== 'assistant' || !msg.content || msg.isStreaming) return;

    const urls = extractUrls(msg.content);

    // Determine base URL for API calls
    let baseUrl = '';
    // Check for Environment Variables
    if (process.env.REACT_APP_API_BASE_URL) {
      baseUrl = process.env.REACT_APP_API_BASE_URL;
    } else if (process.env.REACT_APP_BASE_URL) {
      baseUrl = process.env.REACT_APP_BASE_URL;
    } else if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      baseUrl = 'http://localhost:5000'; // Fallback if no env var
    } else {
      baseUrl = window.location.origin;
    }

    // Ensure no trailing slash
    baseUrl = baseUrl.replace(/\/$/, '');

    // Filter out URLs we've already processed or started processing
    const newUrls = urls.filter(url => !scrapedUrls.current.has(url));

    if (newUrls.length > 0) {
      // Mark as scraping/scraped
      newUrls.forEach(url => scrapedUrls.current.add(url));

      newUrls.forEach(async (url) => {
        // Check if metadata already exists in the message object (from DB persistence)
        if (msg.link_metadata && msg.link_metadata[url]) {
          setLinks(prev => [...prev, msg.link_metadata[url]]);
          return;
        }

        // Otherwise, fetch from scraper (which will now save to DB)
        try {
          const payload = { url };
          if (msg.id) { // Pass messageId if available to save result to DB
            payload.messageId = msg.id;
          }

          const response = await axios.post(`${baseUrl}/api/scraper/preview`, payload);
          if (response.data) {
            setLinks(prev => [...prev, response.data]);
          }
        } catch (error) {
          console.error(`Error fetching link preview for ${url} at ${baseUrl}/api/scraper/preview:`, error);
          if (error.response && error.response.data) {
            setLinks(prev => [...prev, error.response.data]);
          }
        }
      });
    }
  }, [msg.content, msg.role, msg.link_metadata, msg.id, msg.isStreaming]);

  // Handle form messages
  if (msg.role === 'form') {
    return (
      <InlineForm
        key={index}
        form={msg.formData}
        onSubmit={handleFormSubmit}
        botMessageBoxColor={botMessageBoxColor}
        botMessageTextColor={botMessageTextColor}
        userMessageBoxColor={userMessageBoxColor}
        userMessageTextColor={userMessageTextColor}
      />
    );
  }

  // Handle regular messages
  return (
    <div
      key={index}
      className={`chatbot-message ${msg.role}`}
      style={{
        backgroundColor: msg.role === 'user' ? userMessageBoxColor : botMessageBoxColor,
        color: msg.role === 'user' ? userMessageTextColor : botMessageTextColor,
        // Add padding bottom if we have links to separate them visually from bubble edge
        // But links are outside the text bubble usually in WhatsApp? 
        // Actually, WhatsApp puts them inside/attached.
        // Let's render them inside the bubble for now, at the bottom.
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {msg.role === 'assistant' ? (
        <div style={{ color: botMessageTextColor, width: '100%' }} className="markdown">
          {/* Show streaming indicator for empty streaming messages */}
          {msg.isStreaming && msg.content === '' ? (
            <div className="streaming-indicator">
              <div className="typing-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          ) : msg.content.includes('<') && msg.content.includes('>') ? (
            <div
              style={{ color: botMessageTextColor }}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(msg.content, {
                  ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'p', 'br', 'ul', 'ol', 'li', 'a', 'h1', 'h2', 'h3', 'blockquote', 'code', 'pre'],
                  ALLOWED_ATTR: ['href', 'target', 'rel', 'class']
                })
              }}
            />
          ) : (
            <ReactMarkdown
              components={{
                // Simple headings with standard spacing
                h1: ({ node, children, ...props }) => (
                  <h1 style={{ color: botMessageTextColor, fontWeight: 'bold' }} {...props}>
                    {children}
                  </h1>
                ),
                h2: ({ node, children, ...props }) => (
                  <h2 style={{ color: botMessageTextColor, fontWeight: 'bold' }} {...props}>
                    {children}
                  </h2>
                ),
                h3: ({ node, children, ...props }) => (
                  <h3 style={{ color: botMessageTextColor, fontWeight: 'bold' }} {...props}>
                    {children}
                  </h3>
                ),
                // Standard paragraphs
                p: ({ node, children, ...props }) => (
                  <p style={{ color: botMessageTextColor }} {...props}>
                    {children}
                  </p>
                ),
                // Standard lists
                ul: ({ node, ...props }) => <ul style={{ paddingLeft: '20px' }} {...props} />,
                ol: ({ node, ...props }) => <ol style={{ paddingLeft: '20px' }} {...props} />,
                li: ({ node, ...props }) => <li style={{ color: botMessageTextColor }} {...props} />,
                // Standard links
                a: ({ node, children, ...props }) => (
                  <a
                    {...props}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: botMessageTextColor, textDecoration: 'underline' }}
                  >
                    {children}
                  </a>
                ),
                // Standard code
                code: ({ node, inline, className, children, ...props }) =>
                  inline ? (
                    <code style={{ backgroundColor: 'rgba(0,0,0,0.05)', padding: '2px 4px', borderRadius: '3px' }} {...props}>
                      {children}
                    </code>
                  ) : (
                    <pre style={{ backgroundColor: 'rgba(0,0,0,0.05)', padding: '10px', borderRadius: '5px', overflowX: 'auto' }}>
                      <code {...props}>
                        {children}
                      </code>
                    </pre>
                  ),
                // Standard blockquotes
                blockquote: ({ node, children, ...props }) => (
                  <blockquote style={{ borderLeft: '4px solid rgba(0,0,0,0.1)', paddingLeft: '16px', fontStyle: 'italic' }} {...props}>
                    {children}
                  </blockquote>
                ),
              }}
            >
              {msg.content}
            </ReactMarkdown>
          )}
          {/* Show streaming cursor if message is currently streaming and has content */}
          {msg.isStreaming && msg.content && (
            <span className="streaming-cursor">|</span>
          )}

          {/* Render Rich Link Previews */}
          {!msg.isStreaming && links.length > 0 && (
            <div style={{ marginTop: '8px', borderTop: '1px solid rgba(0,0,0,0.05)', paddingTop: '8px' }}>
              <LinkCarousel links={links} />
            </div>
          )}
        </div>
      ) : (
        <div style={{ color: userMessageTextColor }}>{msg.content}</div>
      )}
    </div>
  );
};

const MessageDisplay = ({
  messages,
  botMessageBoxColor,
  botMessageTextColor,
  userMessageBoxColor,
  userMessageTextColor,
  handleFormSubmit
}) => {
  return (
    <>
      {messages.map((msg, index) => (
        <MessageItem
          key={msg.id || msg.timestamp || `msg-${index}-${msg.content?.substring(0, 10)}`}
          index={index}
          msg={msg}
          botMessageBoxColor={botMessageBoxColor}
          botMessageTextColor={botMessageTextColor}
          userMessageBoxColor={userMessageBoxColor}
          userMessageTextColor={userMessageTextColor}
          handleFormSubmit={handleFormSubmit}
        />
      ))}
    </>
  );
};

export default MessageDisplay; 